import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from '@mfw/components';

import type {
  CreateExerciseErrorResponse,
  EditExerciseArgs,
} from '../../api/types/exercises.types';
import type { UiExercise } from '../../schemas/exercises.schema';

import { editExerciseById } from '../../api/exercises.api';
import { applyExerciseEditToWorkoutCaches } from './applyExerciseEdit';
import { getLabelFromTranslations } from '../../utils';
import { useSnackbar } from '../../context';
import { queryKeys } from '../../queryKeys';
import { useMutation } from '../base';
import messages from './messages';

export default function useEditExerciseById() {
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const response = useMutation<
    UiExercise | null,
    CreateExerciseErrorResponse,
    EditExerciseArgs
  >({
    mutationFn: ({ workoutId, ...argsWithoutWorkoutId }: EditExerciseArgs) => {
      return editExerciseById(argsWithoutWorkoutId);
    },
    onSuccess: async (exercise, { locale, organizationId, workoutId }) => {
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.exercises.fetchExercises._def,
      });
      await queryClient.invalidateQueries({
        exact: false,
        queryKey: queryKeys.exercises.fetchInfiniteExercises._def,
      });

      if (!exercise?.id) return;
      await queryClient.setQueryData(
        queryKeys.exercises.fetchExerciseById({
          id: exercise.id,
          locale,
          organizationId,
        }).queryKey,
        exercise,
      );
      enqueueSnackbar({
        message: formatMessage(messages.exerciseEdited, {
          name: getLabelFromTranslations(locale, exercise.translations),
        }),
        variant: 'success',
      });
      if (!workoutId) return;
      applyExerciseEditToWorkoutCaches(queryClient, {
        exercise,
        organizationId,
        workoutDetailDef: queryKeys.workouts.fetchWorkoutById._def,
        workoutId,
      });
    },
  });
  return {
    ...response,
    isLoading: response.status === 'pending',
  };
}
